import { useEffect, useState } from 'react';
import { Button, Center, Group, Loader, Stack, Text } from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { IconCalendarDollar, IconDeviceFloppy } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { SectionCard } from './SectionCard';
import { monthToDate, ym } from '../lib/month';

/** Balanslar paneli ham shu kalit bilan o'qiydi — saqlangach u ham yangilanadi. */
export const DEBT_START_KEY = ['debt-start-month'];

/** Qarz hisobi boshlangan oy (`Tenant.debtStartMonth`, "YYYY-MM" yoki null). */
export function useDebtStartMonth() {
  return useQuery({
    queryKey: DEBT_START_KEY,
    queryFn: async () => (await api.get<{ debtStartMonth: string | null }>('/tenant/debt-start-month')).data.debtStartMonth,
    staleTime: 5 * 60_000,
  });
}

/**
 * QARZ HISOBI BOSHLANGAN OY.
 *
 * Maktab tizimga oy o'rtasida o'tadi va avvalgi to'lovlar tizimda bo'lmaydi. Shu oydan
 * OLDINGI oylar qarzga hisoblanmaydi — aks holda oylik summalar kiritilishi bilan hamma
 * o'quvchi o'tgan oylarning qarzdori bo'lib chiqardi. Bo'sh — chegara yo'q.
 * Backend: `PUT /tenant/debt-start-month` (STAFF), hisob — `lib/balance.hisobdagiOy`.
 */
export function DebtStartSettings() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: current, isLoading } = useDebtStartMonth();
  const [value, setValue] = useState<string>('');

  // Serverdan kelgan qiymat bir marta qo'yiladi (foydalanuvchi tanlovi ustiga yozilmasin)
  const [filled, setFilled] = useState(false);
  useEffect(() => {
    if (filled || current === undefined) return;
    setValue(current ?? '');
    setFilled(true);
  }, [current, filled]);

  const save = useMutation({
    mutationFn: async (debtStartMonth: string | null) =>
      (await api.put<{ debtStartMonth: string | null }>('/tenant/debt-start-month', { debtStartMonth })).data,
    onSuccess: (res) => {
      setValue(res.debtStartMonth ?? '');
      qc.setQueryData(DEBT_START_KEY, res.debtStartMonth);
      // Qarz ko'rinadigan hamma joy shu chegaraga qarab qayta hisoblanadi
      for (const key of ['balances', 'finance', 'cashbook', 'debtors']) qc.invalidateQueries({ queryKey: [key] });
      notifications.show({ color: 'green', message: res.debtStartMonth ? t('debtStart.saved') : t('debtStart.cleared') });
    },
    onError: (e: unknown) =>
      notifications.show({
        color: 'red',
        message: (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? t('common.error'),
      }),
  });

  return (
    <SectionCard title={t('debtStart.section')} subtitle={t('debtStart.sectionDesc')} icon={IconCalendarDollar} maw={520} mb="md">
      {isLoading ? (
        <Center py="md"><Loader size="sm" /></Center>
      ) : (
        <Stack gap="sm">
          <MonthPickerInput
            label={t('debtStart.label')}
            description={t('debtStart.hint')}
            placeholder={t('debtStart.none')}
            value={monthToDate(value)}
            onChange={(d) => setValue(d ? ym(d) : '')}
            valueFormat="YYYY MMMM"
            clearable
          />
          {!value && <Text size="xs" c="dimmed">{t('debtStart.noneHint')}</Text>}
          <Group justify="flex-end">
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              loading={save.isPending}
              disabled={value === (current ?? '')}
              onClick={() => save.mutate(value || null)}
            >
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      )}
    </SectionCard>
  );
}
